import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where, orderBy, DocumentData } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "@/lib/firebase";
import { useUser } from "@/lib/useUser";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface Transaction extends DocumentData {
  id: string;
  dealId?: string;
  buyerId: string;
  sellerId: string;
  statusAvocat: string;
  loiRef?: {
    path: string;
  };
  createdAt: any;
}

const acceptCaseSchema = z.object({
  txId: z.string().min(1, "Transaction ID is required"),
  confirm: z.literal(true, {
    errorMap: () => ({ message: "You must confirm to accept this case" }),
  }),
});

type AcceptCaseForm = z.infer<typeof acceptCaseSchema>;

export default function LawyerCases() {
  const { user, role, loading } = useUser();
  const { toast } = useToast();
  const [rows, setRows] = useState<Transaction[]>([]);
  const [selectedCase, setSelectedCase] = useState<Transaction | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AcceptCaseForm>({
    resolver: zodResolver(acceptCaseSchema),
  });

  useEffect(() => {
    if (!user || (role !== "lawyer" && role !== "avocat" && role !== "admin" && role !== "staff")) {
      return;
    }

    const q = query(
      collection(db, "transactions"),
      where("statusAvocat", "==", "non-assigné"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const transactions = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Transaction[];
        setRows(transactions);
      },
      (error) => {
        console.error("Error fetching cases:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load cases. Please try again.",
        });
      }
    );

    return () => unsub();
  }, [user, role, toast]);

  const openAcceptDialog = (transaction: Transaction) => {
    setSelectedCase(transaction);
    setIsDialogOpen(true);
    reset({ txId: transaction.id, confirm: true });
  };

  const onSubmit = async (data: AcceptCaseForm) => {
    if (!selectedCase) return;

    setIsAccepting(true);
    try {
      const fn = httpsCallable(functions, "lawyerAcceptCase");
      await fn({ txId: data.txId });

      toast({
        title: "Success",
        description: "Case accepted successfully. The case has been assigned to you.",
      });

      setIsDialogOpen(false);
      setSelectedCase(null);
      reset();
    } catch (error: any) {
      console.error("Error accepting case:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to accept case. Please try again.",
      });
    } finally {
      setIsAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || (role !== "lawyer" && role !== "avocat" && role !== "admin" && role !== "staff")) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Access denied. This page is only accessible to lawyers.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground mb-2">Unassigned Cases</h1>
        <p className="text-muted-foreground">Review and accept cases that are waiting for assignment</p>
      </div>

      <div className="space-y-4">
        {rows.map((tx) => (
          <Card key={tx.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <CardTitle className="text-xl">
                Deal: {tx.dealId || "N/A"}
              </CardTitle>
              <CardDescription>
                Case ID: {tx.id}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-semibold">Buyer ID:</span> {tx.buyerId}
                </div>
                <div>
                  <span className="font-semibold">Seller ID:</span> {tx.sellerId}
                </div>
              </div>

              {tx.loiRef?.path && (
                <div className="text-sm text-muted-foreground">
                  <span className="font-semibold">LOI Reference:</span> {tx.loiRef.path}
                </div>
              )}

              <Button
                onClick={() => openAcceptDialog(tx)}
                className="w-full sm:w-auto"
                variant="default"
              >
                Accept This Case
              </Button>
            </CardContent>
          </Card>
        ))}

        {rows.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-muted-foreground">No unassigned cases available at the moment.</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Accept Case Confirmation Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Accept Case</DialogTitle>
            <DialogDescription>
              Are you sure you want to accept this case? This action will assign the case to you.
            </DialogDescription>
          </DialogHeader>

          {selectedCase && (
            <div className="space-y-2 text-sm">
              <div>
                <span className="font-semibold">Deal:</span> {selectedCase.dealId || "N/A"}
              </div>
              <div>
                <span className="font-semibold">Buyer:</span> {selectedCase.buyerId}
              </div>
              <div>
                <span className="font-semibold">Seller:</span> {selectedCase.sellerId}
              </div>
            </div>
          )}

          {errors.confirm && (
            <p className="text-sm text-destructive">{errors.confirm.message}</p>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsDialogOpen(false);
                setSelectedCase(null);
                reset();
              }}
              disabled={isAccepting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              disabled={isAccepting}
            >
              {isAccepting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Accepting...
                </>
              ) : (
                "Confirm & Accept"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
